import { PageHero } from '@/components/PageHero';
import { HeatMatrix } from '@/components/fx/Schematics';
import { TerminalPanel } from '@/components/fx/TerminalPanel';
import { Reveal } from '@/components/fx/Reveal';
import { Headline } from '@/components/fx/Headline';
import { TiltCard } from '@/components/fx/TiltCard';
import type { DataVals } from '@/lib/vals/data';

export function DataPage({ v }: { v: DataVals }) {
  return (
    <main id="wft-main" tabIndex={-1}>
      <PageHero
        eyebrow="Data"
        title="Seventeen connectors, each with its terms attached."
        highlight={['terms', 'attached.']}
        lede="Every connector declares its cadence, whether it needs a key, and whether its data may be re-served. A source marked non-redistributable is display-only: it is excluded from the public API and from exports."
        aside={
          <TerminalPanel
            mnemonic="CTG"
            title="Cross-asset correlation"
            sources={['yfinance', 'CoinGecko']}
          >
            <HeatMatrix />
          </TerminalPanel>
        }
      />

      {/* Tiers */}
      <section className="section">
        <div className="shell">
          <div className="tiers">
            {v.tiers.map((t, i) => (
              <Reveal key={t.label} delay={i * 90}>
                <TiltCard className="tier glass" max={3}>
                  <div className="tier__head">
                    <span className="tier__dot" style={{ background: t.color }} />
                    <h2>{t.label}</h2>
                    <span className="tier__count" style={{ color: t.color }}>
                      {t.count}
                    </span>
                  </div>
                  <p className="tier__body">{t.body}</p>
                  <div className="tier__cells">
                    {t.cells.map((c) => (
                      <span key={c.title}>{c.title}</span>
                    ))}
                  </div>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Registry table */}
      <section className="section">
        <div className="shell">
          <Reveal as="p" className="eyebrow">
            The registry
          </Reveal>
          <Headline
            as="h2"
            className="display display--section section__title"
            text="Every source, with its terms in the open."
            highlight={['open.']}
          />

          {/* WCAG 2.1.1: the table scrolls sideways on narrow viewports and holds
              nothing focusable, so it needs its own tab stop to be pannable. */}
          <Reveal
            className="table-wrap glass"
            tabIndex={0}
            role="region"
            aria-label="Connector registry"
          >
            <table className="src-table">
              <caption className="wft-sr-only">
                Connectors with cadence, key requirement, redistribution terms and the sections they
                feed
              </caption>
              <thead>
                <tr>
                  <th scope="col">Source</th>
                  <th scope="col">Cadence</th>
                  <th scope="col">Key</th>
                  <th scope="col">Redistribution</th>
                  <th scope="col">Feeds</th>
                </tr>
              </thead>
              <tbody>
                {v.sources.map((s) => (
                  <tr key={s.label}>
                    <th scope="row">
                      {s.label}
                      <em>{s.note}</em>
                    </th>
                    <td>
                      <code>{s.cadence}</code>
                    </td>
                    <td>
                      <code>{s.key}</code>
                    </td>
                    <td style={{ color: s.redistColor }}>{s.redist}</td>
                    <td className="src-table__feeds">{s.feeds}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>
        </div>
      </section>

      {/* States */}
      <section className="section">
        <div className="shell">
          <Reveal as="p" className="eyebrow">
            Six states, and what each does to the screen
          </Reveal>
          <Headline
            as="h2"
            className="display display--section section__title"
            text="Not connected is a state, not an error."
            highlight={['state,']}
          />
          <div className="states">
            {v.states.map((s, i) => (
              <Reveal key={s.name} delay={i * 70}>
                <div className="state glass">
                  <span
                    className="state__dot"
                    style={{
                      background: s.fill,
                      boxShadow: s.ring === 'none' ? undefined : s.ring,
                    }}
                  />
                  <h3 style={{ color: s.color }}>{s.name}</h3>
                  <p>{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Env keys */}
      <section className="section">
        <div className="shell split">
          <div className="split__lead">
            <Reveal as="p" className="eyebrow">
              What a key unlocks
            </Reveal>
            <Headline
              as="h2"
              className="display display--section"
              text="Blank disables a connector cleanly."
              highlight={['cleanly.']}
            />
            <Reveal as="p" className="lede" delay={200}>
              Leaving a variable empty is a supported configuration. The panels it would have fed
              say which key is missing rather than breaking.
            </Reveal>
          </div>
          <div className="fields">
            {v.envKeys.map((k, i) => (
              <Reveal key={k.env} delay={i * 70}>
                <div className="field glass">
                  <code>{k.env}</code>
                  <p>{k.gives}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
